import { Factory, Formatter, Stave, Voice } from 'vexflow';
import type { EarTrainingClip, EarTrainingNote } from '../types/earTraining';
import { midiToEasyScorePitch } from './pitchLabel';

const MEASURE_BEATS = 4;
const STAVE_X = 12;
const STAVE_Y_START = 36;
const ROW_HEIGHT = 72;
const STAVE_WIDTH_MIN = 200;
const STAVE_WIDTH_MAX = 360;

/** Map beat length to EasyScore duration token (w, h, q., q, 8, …). */
export function beatsToEasyDuration(beats: number): string {
  const b = Math.round(beats * 100) / 100;
  if (b >= 3.5) return 'w';
  if (b >= 2.5) return 'h';
  if (b >= 1.75) return 'h';
  if (b >= 1.25) return 'q.';
  if (b >= 0.75) return 'q';
  if (b >= 0.35) return '8';
  return '16';
}

/** Duration token → beats (for measure validation). */
export function easyDurationToBeats(dur: string): number {
  if (dur === 'w') return 4;
  if (dur === 'h' || dur === 'h.') return dur === 'h.' ? 3 : 2;
  if (dur === 'q.' || dur === 'qd') return 1.5;
  if (dur === 'q') return 1;
  if (dur === '8') return 0.5;
  if (dur === '16') return 0.25;
  return 1;
}

function restForBeats(beats: number): string {
  const dur = beatsToEasyDuration(beats);
  return `b4/${dur}/r`;
}

function noteSpec(midi: number, beats: number, fifths: number): string {
  const pitch = midiToEasyScorePitch(midi, fifths);
  const dur = beatsToEasyDuration(beats);
  return `${pitch}/${dur}`;
}

/** Split notes into 4/4 EasyScore measure strings (pad with rests). */
export function buildMeasureNoteSpecs(
  notes: EarTrainingNote[],
  transpose: number,
  keySignatureFifths: number
): string[] {
  const measures: string[] = [];
  let parts: string[] = [];
  let beatsInMeasure = 0;

  const flushMeasure = () => {
    if (parts.length === 0) return;
    const pad = MEASURE_BEATS - beatsInMeasure;
    if (pad > 0.01) parts.push(restForBeats(pad));
    measures.push(parts.join(', '));
    parts = [];
    beatsInMeasure = 0;
  };

  for (const n of notes) {
    let remaining = n.beats;
    const midi = n.midi + transpose;
    while (remaining > 0.01) {
      const space = MEASURE_BEATS - beatsInMeasure;
      const take = Math.min(remaining, space);
      parts.push(noteSpec(midi, take, keySignatureFifths));
      beatsInMeasure += take;
      remaining -= take;
      if (beatsInMeasure >= MEASURE_BEATS - 0.01) flushMeasure();
    }
  }
  flushMeasure();
  return measures;
}

/** Sum beats in a measure string (for validation). */
export function measureTotalBeats(measureSpec: string): number {
  let total = 0;
  for (const piece of measureSpec.split(',')) {
    const t = piece.trim();
    if (t.includes('/r')) {
      const m = /\/([whq.8]+)\/r/.exec(t) ?? /\/([whq.8]+)r/.exec(t);
      if (m) total += easyDurationToBeats(m[1]!);
    } else {
      const m = /\/([whq.8]+)$/.exec(t);
      if (m) total += easyDurationToBeats(m[1]!);
    }
  }
  return total;
}

/** Fit staff canvas to the reveal panel (keeps SVG on screen). */
export function computeStaffDimensions(
  measureCount: number,
  maxWidth: number
): { width: number; height: number } {
  const cap = Math.max(STAVE_WIDTH_MIN, Math.floor(maxWidth));
  const staveWidth = Math.min(STAVE_WIDTH_MAX, Math.max(STAVE_WIDTH_MIN, cap - STAVE_X * 2));
  const width = Math.min(cap, staveWidth + STAVE_X * 2);
  const height = STAVE_Y_START + measureCount * ROW_HEIGHT + 16;
  return { width, height };
}

function staffMaxWidth(container: HTMLElement): number {
  const fromContainer = container.clientWidth;
  if (fromContainer > 0) return fromContainer;
  if (typeof window !== 'undefined') {
    return Math.min(STAVE_WIDTH_MAX, Math.max(STAVE_WIDTH_MIN, window.innerWidth - 48));
  }
  return STAVE_WIDTH_MAX;
}

/** Render treble staff into container (clears element first). */
export function renderEarTrainingStaff(container: HTMLElement, clip: EarTrainingClip): void {
  container.innerHTML = '';
  const fifths = clip.keySignatureFifths;
  const measures = buildMeasureNoteSpecs(clip.notes, clip.transposeSemitones, fifths);
  if (measures.length === 0) return;

  const panelWidth = staffMaxWidth(container);
  const staveWidth = Math.min(STAVE_WIDTH_MAX, Math.max(STAVE_WIDTH_MIN, panelWidth - STAVE_X * 2));
  const { width, height } = computeStaffDimensions(measures.length, panelWidth);
  const formatWidth = staveWidth - Stave.defaultPadding - 10;

  const host = document.createElement('div');
  host.className = 'ear-training-staff-host';
  host.id = `ear-staff-${Math.random().toString(36).slice(2, 9)}`;
  host.style.maxWidth = '100%';
  container.appendChild(host);

  const vf = new Factory({
    renderer: { elementId: host.id, width, height },
  });
  const score = vf.EasyScore();

  measures.forEach((noteSpecLine, i) => {
    const y = STAVE_Y_START + i * ROW_HEIGHT;
    const stave = vf.Stave({ x: STAVE_X, y, width: staveWidth });
    const tickables = score.notes(noteSpecLine, { stem: 'up' });
    const voice = vf.Voice({ time: '4/4' }).setMode(Voice.Mode.SOFT).addTickables(tickables);
    if (i === 0) {
      stave.addClef('treble').addTimeSignature('4/4');
    }
    new Formatter().joinVoices([voice]).format([voice], formatWidth);
  });

  vf.draw();
}
